/**
 * RewardsBlock: two once-ever social rewards shown under the PM balance row in
 * the Profile tab. Amounts come from the shared billing constants.
 *
 *  - Join the Discord: an always-visible "Join server" link (its own click
 *    gesture) takes them to the invite; the "Verify" button runs the
 *    OAuth `identify` popup, and the server confirms guild membership + credits.
 *    We open ONE window per gesture (a second `window.open` in the same handler
 *    is popup-blocked), so joining and verifying are deliberately two actions.
 *  - Tweet about Polymyth: records a claim and shows the pre-filled post text
 *    in-place with a copy button (plus a real link to X as a fallback); Tavian's
 *    daily X pass verifies + credits asynchronously. We used to drive this
 *    via `window.open` into X's compose-intent page, but that popup closes
 *    itself unpredictably (X only tolerates it as a sized popup opened directly
 *    by the gesture, not a full tab whose location gets reassigned later), so a
 *    reliable in-place copy panel replaces it. The pending state keeps a "Tweet
 *    again" affordance so a user who never posted isn't stranded.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, Copy, Share2, Users } from 'lucide-react';
import { apiClient, type RewardState } from '@/lib/api';
import { DiscordOAuthError, openDiscordOAuthPopup } from '@/lib/discord-oauth';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { DISCORD_JOIN_GRANT_PM, X_REVIEW_GRANT_PM } from '@danda/shared';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/lib/stores/authStore';
import { SaveAccountCard } from '@/components/account/SaveAccountCard';

const POLYMYTH_URL = 'https://polymyth.quest';
const X_HANDLE = 'avive';

function buildTweetText(characterName: string): string {
  return `I'm playing as ${characterName} on Polymyth, an AI-run role-playing adventure by @${X_HANDLE}. Come roll with me: ${POLYMYTH_URL}`;
}

function buildTweetIntentUrl(text: string): string {
  return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
}

interface RewardsBlockProps {
  /** Called after a Discord credit lands, so the parent can refresh the balance. */
  onCredited: () => void;
}

export function RewardsBlock({ onCredited }: RewardsBlockProps) {
  const { user } = useAuthStore();
  const [discord, setDiscord] = useState<RewardState>('unclaimed');
  const [xReview, setXReview] = useState<RewardState>('unclaimed');
  const [discordLoading, setDiscordLoading] = useState(false);
  const [xLoading, setXLoading] = useState(false);
  const [tweetPanel, setTweetPanel] = useState<{ text: string; url: string } | null>(null);

  // Fetch reward status fresh on mount (X review flips to credited async).
  useEffect(() => {
    if (user?.isAnonymous) return;
    let cancelled = false;
    apiClient
      .getRewardsStatus()
      .then(({ discord, xReview }) => {
        if (cancelled) return;
        setDiscord(discord);
        setXReview(xReview);
      })
      .catch(() => {
        /* leave defaults; the buttons still work */
      });
    return () => {
      cancelled = true;
    };
  }, [user?.isAnonymous]);

  // Guests can't earn rewards (server returns 403). Instead of hiding the
  // block, show the rewards as a preview with an in-place save-account CTA:
  // converting flips isAnonymous and this re-renders into the live buttons.
  if (user?.isAnonymous) {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <div className="text-sm font-medium text-muted-foreground">Earn PMT tokens</div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Users className="h-4 w-4 shrink-0 text-indigo-400" />
            <span>Join the Discord</span>
          </div>
          <span className="text-sm text-muted-foreground">+{DISCORD_JOIN_GRANT_PM} PMT</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-foreground">
            <Share2 className="h-4 w-4 shrink-0 text-sky-400" />
            <span>Tweet about Polymyth</span>
          </div>
          <span className="text-sm text-muted-foreground">+{X_REVIEW_GRANT_PM} PMT</span>
        </div>
        <SaveAccountCard
          title="Save your account to claim these"
          description="You're playing as a guest. Add an email and password (or use Google) to keep your hero and unlock these rewards."
        />
      </div>
    );
  }

  const handleVerifyDiscord = () => {
    if (discordLoading || discord === 'credited') return;
    // openDiscordOAuthPopup opens its window synchronously (the required
    // gesture); it's the ONLY window we open in this handler.
    const codePromise = openDiscordOAuthPopup();

    setDiscordLoading(true);
    codePromise
      .then((code) => apiClient.discordOAuthCallback(code))
      .then((res) => {
        if (res.status === 'credited') {
          setDiscord('credited');
          toast.success(`+${DISCORD_JOIN_GRANT_PM} PMT for joining the Discord!`);
          onCredited();
        } else if (res.status === 'already_claimed') {
          // Reward landed on an earlier call, so no toast: we didn't just grant
          // anything. Still refresh, because the earlier grant's response may be
          // what went missing (retries: 0), leaving the gate store behind the
          // balance the server actually holds.
          setDiscord('credited');
          onCredited();
        } else if (res.status === 'discord_already_used') {
          toast.error('This Discord account already claimed the reward on another account.');
        } else {
          toast.error("Not in the server yet. Tap 'Join server' first, then verify.");
        }
      })
      .catch((e: unknown) => {
        // A user-cancelled popup is not an error worth a toast.
        if (e instanceof DiscordOAuthError && /cancel/i.test(e.message)) return;
        toast.error(e instanceof Error ? e.message : 'Discord sign-in failed');
      })
      .finally(() => setDiscordLoading(false));
  };

  const handleTweet = () => {
    if (xLoading) return;
    setXLoading(true);
    apiClient
      .claimXReviewIntent()
      .then((res) => {
        const text = buildTweetText(res.characterName);
        setTweetPanel({ text, url: buildTweetIntentUrl(text) });
        setXReview(res.status === 'credited' ? 'credited' : 'pending');
        // The X grant lands out-of-band (Tavian's daily pass), so a claim that
        // comes back credited means tokens arrived since this session started.
        if (res.status === 'credited') onCredited();
      })
      .catch(() => {
        toast.error('Create a character first, then share your review.');
      })
      .finally(() => setXLoading(false));
  };

  const handleCopyTweetText = async () => {
    if (!tweetPanel) return;
    try {
      await navigator.clipboard.writeText(tweetPanel.text);
      toast.success('Copied! Paste it into a post on X.');
    } catch {
      toast.error('Copy failed, select the text manually.');
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/40 px-4 py-3">
      <div className="text-sm font-medium text-muted-foreground">Earn PMT tokens</div>

      {/* Discord: join (own gesture) + verify. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Users className="h-4 w-4 shrink-0 text-indigo-400" />
          <span>Join the Discord</span>
        </div>
        {discord === 'credited' ? (
          <ClaimedBadge amount={DISCORD_JOIN_GRANT_PM} />
        ) : (
          <div className="flex items-center gap-2">
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Join server ↗
            </a>
            <Button
              size="sm"
              variant="outline"
              onClick={handleVerifyDiscord}
              disabled={discordLoading}
            >
              {discordLoading ? 'Working…' : `Verify (+${DISCORD_JOIN_GRANT_PM} PMT)`}
            </Button>
          </div>
        )}
      </div>

      {/* X review: tweet, then Tavian credits async. Pending keeps a re-tweet. */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-foreground">
          <Share2 className="h-4 w-4 shrink-0 text-sky-400" />
          <span>Tweet about Polymyth</span>
        </div>
        {xReview === 'credited' ? (
          <ClaimedBadge amount={X_REVIEW_GRANT_PM} />
        ) : (
          <div className="flex items-center gap-2">
            {xReview === 'pending' && (
              <span className="text-sm text-muted-foreground">Pending review…</span>
            )}
            <Button size="sm" variant="outline" onClick={handleTweet} disabled={xLoading}>
              {xLoading
                ? 'Working…'
                : xReview === 'pending'
                  ? 'Tweet again'
                  : `Tweet (+${X_REVIEW_GRANT_PM} PMT)`}
            </Button>
          </div>
        )}
      </div>

      {tweetPanel && xReview !== 'credited' && (
        <div className="space-y-2 rounded-md border border-border bg-background p-3">
          <p className="text-sm text-foreground">{tweetPanel.text}</p>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={handleCopyTweetText}>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy text
            </Button>
            <a
              href={tweetPanel.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Post on X ↗
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function ClaimedBadge({ amount }: { amount: number }) {
  return (
    <span className="flex items-center gap-1 text-sm font-medium text-green-600">
      <Check className="h-4 w-4" />+{amount} PMT claimed
    </span>
  );
}
